import { Callback, Dict } from '@/types/utility';
import SocketCentrifugeBase, { SubscriptionError } from '@/api/sockets/SocketCentrifugeBase';

export enum DeviceSocketEvents {
  DOMP_DESCRIPTION = 'device.dompDescription',
  COMMAND_RESPONSE = 'device.command.response',
  ERROR = 'device.error',
  NETWORK_DETAILS = 'device.networkDetails',
}

enum DeviceSocketRPC {
  COMMAND = 'device.command',
  GET_DOMP_DESCRIPTION = 'device.getDompDescription',
}

export default class DeviceSocket extends SocketCentrifugeBase<
  DeviceSocketEvents,
  DeviceSocketRPC
> {
  private static connections: Dict<DeviceSocket> = {};

  public deviceId: string;

  protected listeners: Record<DeviceSocketEvents, Callback[]> = {
    [DeviceSocketEvents.DOMP_DESCRIPTION]: [],
    [DeviceSocketEvents.COMMAND_RESPONSE]: [],
    [DeviceSocketEvents.ERROR]: [],
    [DeviceSocketEvents.NETWORK_DETAILS]: [],
  };

  private constructor(
    public id: string,
    onConnect?: Callback<[SocketCentrifugeBase<DeviceSocketEvents, DeviceSocketRPC>]>,
    onError?: Callback<[SubscriptionError]>,
  ) {
    super(`personal:device-${id}`, {
      handleEvents: ({ data }) => {
        switch (data.event) {
          case DeviceSocketEvents.DOMP_DESCRIPTION:
            this.callListeners(
              DeviceSocketEvents.DOMP_DESCRIPTION,
              data.serial_device_id,
              data.data,
            );
            break;
          case DeviceSocketEvents.NETWORK_DETAILS:
            this.callListeners(DeviceSocketEvents.NETWORK_DETAILS, data);
            break;
          case DeviceSocketEvents.COMMAND_RESPONSE:
            this.callListeners(
              DeviceSocketEvents.COMMAND_RESPONSE,
              data.from_who,
              data.response,
              data.varStr,
            );
            break;
          case DeviceSocketEvents.ERROR:
            this.callListeners(DeviceSocketEvents.ERROR, data.error);
            break;
        }
      },
      onConnect,
      onError,
    });

    this.deviceId = id;
    this.dataAddedToEveryRequest = {
      device_id: this.deviceId,
    };
  }

  public static start(
    id: string,
    onConnect?: Callback<[SocketCentrifugeBase<DeviceSocketEvents, DeviceSocketRPC>]>,
    onError?: Callback<[SubscriptionError]>,
  ) {
    const connection = DeviceSocket.connections[id];
    if (connection) {
      connection.addSession();
      onConnect?.(connection);
      return connection;
    }

    DeviceSocket.connections[id] = new DeviceSocket(id, onConnect, onError);
    return DeviceSocket.connections[id];
  }

  close(listenersGroupId: string): boolean {
    const isClosed = super.close(listenersGroupId);

    if (isClosed) {
      delete DeviceSocket.connections[this.id];
    }

    return isClosed;
  }

  public command(to, name, payload) {
    return this.sendNamedRPC(DeviceSocketRPC.COMMAND, { to, name, payload });
  }

  public getDompDescription(serialDeviceId) {
    return this.sendNamedRPC(DeviceSocketRPC.GET_DOMP_DESCRIPTION, {
      serial_device_id: serialDeviceId,
    });
  }
}
